/**
 * สัญญากับ CRM module ของ HIS — **ตรวจกับ staging จริงแล้วทุกฟิลด์**
 *
 * ต่างจาก `src/api/contracts.ts` ซึ่งเป็น "ข้อเสนอ" ที่เราเขียนขึ้นเอง
 * ไฟล์นี้คือของที่ backend ทำไว้แล้วและใช้งานได้จริง
 *
 *   base path   /apis/CRM/
 *   division    HTTP header `Division: 545`
 *   ตรวจเมื่อ    25 สิงหาคม 2569 (backend อัปเดตรอบใหญ่ — ดู CHANGELOG ท้ายไฟล์)
 *
 * ⚠️ **รูปแบบ pagination ไม่ใช่ DRF มาตรฐาน**
 *    ของจริงคือ { total, next, previous, items }
 *    ไม่ใช่      { count, next, previous, results } ตามที่ crm_openapi.json ระบุ
 *    ตกลงกับทีม backend แล้วว่ายึดของจริง แล้วจะแก้ spec ตาม
 */

/** ห่อรายการของ CRM — คีย์ต่างจาก DRF มาตรฐาน ดูหมายเหตุด้านบน */
export interface CrmPage<T> {
  total: number;
  next: string | null;
  previous: string | null;
  items: T[];
}

/* ==========================================================================
   คิวตรวจคำขอสะสมคะแนน
   ========================================================================== */

export type CrmClaimStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type CrmClaimType = 'RECEIPT' | 'BANK_TRANSFER' | 'PLATFORM' | 'OTHER';
export type CrmPlatform = 'SHOPEE' | 'LAZADA' | 'TIKTOK';

/**
 * การกระทำกับคำขอ
 *
 * 🔴 **ต้องส่งเป็น "ชื่อ" ของ enum ไม่ใช่ "ค่า" ที่เก็บในฐานข้อมูล**
 *
 *    backend ค้นด้วย **ชื่อ** ของการกระทำ
 *      'APPROVE' ✅   'A' ⛔ → 400 {"action":["invalid action"]}
 *    ตัวพิมพ์ใหญ่เล็กมีผล — 'Approve' ก็ไม่ผ่าน
 *    (เจอจริง: รอบแรกส่งรหัสย่อ 'A' แล้วได้ 400 ทุกครั้ง)
 *
 * state machine ที่ backend บังคับ
 *   (ไม่มี)  --CREATE-->   PENDING
 *   PENDING  --EDIT-->     PENDING       (แก้ยอด ไม่เปลี่ยนสถานะ — ใหม่)
 *   PENDING  --APPROVE-->  APPROVED      PENDING  --REJECT--> REJECTED
 *   APPROVED --ROLLBACK--> PENDING       REJECTED --ROLLBACK--> PENDING
 *   ผิดกติกาจะได้ 400 พร้อม code: ACTION_NOT_ALLOWED
 */
export const CRM_ACTION = {
  CREATE: 'CREATE',
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  ROLLBACK: 'ROLLBACK',
  /** แก้ยอดโดยไม่ตัดสิน — PENDING → EDIT → PENDING (FR-ADM-12) */
  EDIT: 'EDIT',
} as const;
export type CrmAction = (typeof CRM_ACTION)[keyof typeof CRM_ACTION];

/** ผู้ใช้แบบย่อที่ backend ฝังมาให้ในคำขอและใน action log */
export interface CrmMiniUser {
  id: number;
  username: string;
  email: string;
}

/**
 * บัญชีสมาชิกแบบย่อที่ฝังมากับคำขอ
 *
 * ✅ **`full_name` คือของใหม่ที่แก้ปัญหาใหญ่ที่สุด**
 *    backend ประกอบชื่อให้จาก patient ก่อน ถ้าว่างจึงใช้ profile ของ user
 *    ถ้าว่างอีกจึงตกมาที่ username — ไม่มีทางได้ค่าว่าง
 *    ทำให้ไม่ต้องดึง loyalty-account ทั้ง division มา join เองอีกต่อไป
 */
export interface CrmMiniAccount {
  id: number;
  user: number | null;
  patient: number | null;
  full_name: string;
}

/**
 * ⚠️ ฟิลด์ที่ **ยังไม่มี** ใน CRM แต่หน้าจอของเราต้องใช้ (ดู เอกสารวิเคราะห์ช่องว่างของ API ที่ทีมเก็บไว้ภายใน)
 *    ยอดที่เจ้าหน้าที่แก้ (แยกจากยอดเดิม) · ผู้รับผิดชอบ ·
 *    สิ่งที่ระบบตรวจพบ · รหัสคำขอที่อ่านออก · ภาพย่อของสลิป
 *
 * 🔴 **breaking change 25 ส.ค. 2569** — สามฟิลด์นี้เคยเป็นเลข id / string
 *    `account` · `approved_by` · `rejected_by` · `rollback_by`
 *    ตอนนี้เป็น object ทั้งหมด โค้ดที่อ่านเป็นเลขจะพังเงียบ ๆ
 */
export interface CrmClaim {
  id: number;
  type: CrmClaimType;
  platform: CrmPlatform | null;
  status: CrmClaimStatus;
  amount: string;
  created: string;
  edited: string;
  image: string | null;
  /** สาขา/ที่ตั้งที่ซื้อ — ข้อความอิสระ ว่างได้ */
  order_location: string;
  order_id: string | null;
  order_at: string | null;
  approved_at: string | null;
  rollback_at: string | null;
  rejected_at: string | null;
  rejected_reason: string;
  account: CrmMiniAccount;
  approved_by: CrmMiniUser | null;
  rollback_by: CrmMiniUser | null;
  rejected_by: CrmMiniUser | null;
}

/** ตัวกรองที่ backend รองรับ **จริง** — ตรงกับ crm_openapi.json 25 ส.ค. 2569 */
export interface CrmClaimQuery {
  status?: CrmClaimStatus;
  account?: number;
  /**
   * ✅ ใหม่ — ค้นชื่อสมาชิกฝั่ง server
   *
   * backend ค้นข้าม 6 คอลัมน์: ชื่อ/นามสกุลใน profile ของ user
   * และชื่อ/นามสกุลของ patient ทั้งภาษาไทยและอังกฤษ (icontains ทุกตัว)
   *
   * ⚠️ **ค้นได้เฉพาะชื่อคน** ไม่ครอบคลุมรหัสคำขอหรือเลขคำสั่งซื้อ
   */
  name?: string;
  /** ✅ ใหม่ — ช่วงยอดเงิน */
  amount__gte?: number;
  amount__lte?: number;
  amount__gt?: number;
  amount__lt?: number;
  ordering?: string;
  limit?: number;
  offset?: number;
  /* ⛔ ยังไม่มี: ค้นรหัสคำขอ · เลขคำสั่งซื้อ · type · platform · ช่วงวันที่ */
}

/**
 * payload ของ PATCH — ใช้ทั้งอนุมัติ ปฏิเสธ และย้อนกลับ
 *
 * ⚠️ **ตอนอนุมัติต้องส่ง `amount` มาด้วยเสมอ**
 *    ไม่งั้นได้ 400 `{"amount": ["Amount is required"]}`
 *    เพราะ backend ต้องใช้ยอดไปคูณกับ earning policy เพื่อคำนวณคะแนน
 *
 *    ผลพลอยได้ที่สำคัญ: **`amount` เขียนทับได้ตอนอนุมัติ**
 *    แปลว่าการที่เจ้าหน้าที่แก้ยอดก่อนอนุมัติ (FR-ADM-12) **ทำได้จริง**
 *    ต่างจากที่รายงานไว้รอบแรกว่าทำไม่ได้
 *
 *    ข้อจำกัดที่ยังเหลือ: ไม่มีที่เก็บ "ยอดที่ลูกค้ากรอกตอนแรก" แยกจาก "ยอดที่แก้แล้ว"
 *    แก้แล้วยอดเดิมหายไปเลย ตรวจย้อนหลังไม่ได้ว่าแก้จากเท่าไร
 */
export interface CrmClaimAction {
  action: CrmAction;
  amount?: string;
  rejected_reason?: string;
}

/* ==========================================================================
   บัญชีสมาชิก
   ========================================================================== */

export interface CrmTierRef {
  id: number;
  name: string;
  start_point: string;
  end_point: string | null;
  division: number;
  benefit_codes: string[];
}

export interface CrmExpiringPoints {
  id: number;
  total_amount: string;
  expired_at: string;
}

/**
 * ✅ **มี `full_name` แล้ว** (25 ส.ค. 2569) — เลิกต้องยิงต่อไปที่ users/patient
 *
 * ⚠️ ยังไม่มี "รหัสสมาชิก" ที่อ่านออก — ฝั่งเราประกอบจาก id เอง
 */
export interface CrmAccount {
  id: number;
  full_name: string;
  tier_points: string;
  reward_points: string;
  current_tier: CrmTierRef | null;
  next_tier: CrmTierRef | null;
  next_expire_tier_points: CrmExpiringPoints[];
  next_expire_reward_points: CrmExpiringPoints[];
  user: number | null;
  patient: number;
  division: number;
}

/* ==========================================================================
   ระดับสมาชิกและสิทธิประโยชน์
   ========================================================================== */

/** รหัสสิทธิ์ — เป็นชุดตายตัวของ backend เพิ่มเองไม่ได้ */
export type CrmBenefitCodeValue = 'D03' | 'D05' | 'D10' | 'D15' | 'D20' | 'D25' | 'FS';

export interface CrmBenefitCode {
  id: number;
  code: CrmBenefitCodeValue;
  name: string;
  description: string;
  display_seq: number;
  is_active: boolean;
  is_highlight: boolean;
}

/**
 * ⚠️ `benefit_codes` และ `active_benefit_codes` เป็น **readOnly ทั้งคู่**
 *    ผูกสิทธิ์เข้า tier ผ่าน API ไม่ได้ — ตกลงกันแล้วว่าผูกผ่านหน้าผู้ดูแลระบบของ backend ไปก่อน
 *    ฝั่งเราจึงแสดงผลอย่างเดียว ไม่มีปุ่มแก้สิทธิ์
 *
 * `end_point` backend คำนวณให้จาก start_point ของระดับถัดไป
 * ตรงกับตรรกะ ceilingOf() ที่หน้าจอเราเขียนไว้พอดี
 */
export interface CrmTier {
  id: number;
  name: string;
  start_point: string;
  end_point: string | null;
  division: number;
  benefit_codes: string[];
  active_benefit_codes: CrmBenefitCode[];
}

/**
 * ฟิลด์ที่เขียนได้ตอนสร้าง/แก้
 *
 * ⚠️ `division` เป็น read-only แล้ว — backend เติมเองจาก header `Division`
 *    ส่งไปก็ถูกทิ้ง ไม่ต้องส่ง
 */
export interface CrmTierWrite {
  name: string;
  start_point: string;
}

/* ==========================================================================
   กติกาคะแนน
   ========================================================================== */

/**
 * CRM แยกคะแนนเป็นสองชนิดซึ่งยืดหยุ่นกว่าที่ SRS ออกแบบไว้
 *   tier_*   คะแนนสำหรับเลื่อนระดับ
 *   reward_* คะแนนสำหรับแลกของรางวัล
 *
 * ⚠️ ฟิลด์ที่ SRS 3.4 กำหนดแต่ CRM ยังไม่มี — ปัดเศษ · เก็บเศษ · แจ้งเตือนก่อนหมดอายุ ·
 *    รอบรีเซ็ตยอดสะสม · นโยบายเมื่อรีเซ็ต · ระยะผ่อนผัน · ส่วนลดแรกเข้า
 */
export interface CrmEarningPolicy {
  id: number;
  created: string;
  edited: string;
  /** นโยบายสำรอง — ใช้เมื่อไม่มีตัวไหนตรงช่วงเวลา และไม่สนใจ start/end date */
  default: boolean;
  start_date: string | null;
  end_date: string | null;
  tier_multiplier: string;
  tier_duration_days: number;
  reward_multiplier: string;
  reward_duration_days: number;
  edit_user: string | null;
  division: number;
}

export type CrmEarningPolicyWrite = Partial<
  Pick<CrmEarningPolicy,
    'default' | 'start_date' | 'end_date' |
    'tier_multiplier' | 'tier_duration_days' |
    'reward_multiplier' | 'reward_duration_days'>
> & { division: number };

/* ==========================================================================
   ประวัติการตรวจ (point-claim-action-log)
   ========================================================================== */

/** คำขอแบบย่อที่ฝังมาใน log — ไม่มี amount และไม่มีรูปสลิป */
export interface CrmMiniClaim {
  id: number;
  account: CrmMiniAccount;
  status: CrmClaimStatus;
  type: CrmClaimType;
  platform: CrmPlatform | null;
  order_id: string | null;
  order_location: string;
  order_at: string | null;
}

/**
 * รายการเดียวในประวัติการตรวจ
 *
 * `statable` คือคำขอที่ถูกกระทำ (ชื่อมาจาก BaseActionLog ของ framework)
 * `datetime` ใช้ `auto_now` ไม่ใช่ `auto_now_add` — เป็นเวลาที่บันทึกถูกเขียนล่าสุด
 *
 * ⚠️ **endpoint นี้ไม่รับพารามิเตอร์กรองเลย** กรองตามคำขอทีละใบไม่ได้
 *    รับได้แค่ `limit` · `offset` · `ordering`
 *    ฝั่งเราจึงต้องดึงมาแล้วกรองด้วย statable.id ในเครื่อง
 *    ถ้าจำนวน log โตขึ้นต้องขอ backend เพิ่ม `?statable=` ให้
 */
export interface CrmClaimActionLog {
  id: number;
  action: CrmAction;
  statable: CrmMiniClaim;
  user: CrmMiniUser | null;
  datetime: string;
  comment: string;
}

/* ==========================================================================
   ของรางวัล (loyalty-reward)
   ========================================================================== */

/**
 * ⚠️ **สิ่งที่งานออกแบบมีแต่ CRM ยังไม่มี**
 *    รูปของรางวัล · จำนวนคงเหลือ/โควตา · หมวดหมู่ · ลิมิตต่อคน · ธงเปิด/ปิด
 *
 *    การเปิด/ปิดใช้ช่วงเวลาแทน — `start_datetime`/`end_datetime` เป็น null
 *    แปลว่า "ไม่จำกัดช่วง" ซึ่งเท่ากับเปิดตลอด
 */
export interface CrmReward {
  id: number;
  division: number;
  name: string;
  description: string;
  /** คะแนนที่ต้องใช้แลก */
  points: string;
  start_datetime: string | null;
  end_datetime: string | null;
  created: string;
  edited: string;
  edit_user: string | null;
}

/** ⚠️ `division` read-only เหมือน tier — เติมจาก header `Division` */
export type CrmRewardWrite = Partial<
  Pick<CrmReward, 'name' | 'description' | 'points' | 'start_datetime' | 'end_datetime'>
>;

/** ตัวกรองที่ endpoint ของรางวัลรองรับจริง */
export interface CrmRewardQuery {
  name__icontains?: string;
  points__gte?: number;
  points__lte?: number;
  start_datetime__gte?: string;
  start_datetime__lte?: string;
  end_datetime__gte?: string;
  end_datetime__lte?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
}

/* ==========================================================================
   ตัวเลขสรุปสำหรับหน้ารวม
   ========================================================================== */

/**
 * สรุปคะแนนสะสมทั้ง division — GET /loyalty-point-summary/?expire_days=90
 *
 * นับเฉพาะก้อนคะแนนที่**ยังไม่หมดอายุ** (`filter_available()`)
 * `total_points` คือภาระผูกพันคะแนนคงค้าง = Point Liability ใน FR-RPT-06
 *
 * ⚠️ เป็นคะแนน **reward** อย่างเดียว ไม่รวมคะแนนสำหรับเลื่อนระดับ (tier points)
 */
export interface CrmPointSummary {
  total_points: string;
  total_accounts: number;
  expiring_points: string;
  /** สะท้อนค่าที่ส่งไป ไม่ได้ส่งไปจะเป็น 90 */
  expire_days: number;
}

/**
 * จำนวนสมาชิกต่อระดับ — GET /loyalty-tier-summary/
 *
 * ⚠️ **ไม่ห่อ pagination** ตอบเป็น array ตรง ๆ (`pagination_class = None`)
 *    อย่าไปอ่าน `.items` จะได้ undefined
 *
 * backend นับจากคะแนน tier ที่ยังไม่หมดอายุ เทียบกับช่วง start_point–end_point
 * เรียงตาม start_point จากน้อยไปมาก
 */
export interface CrmTierSummary {
  tier: CrmTierRef;
  total_accounts: number;
}

/* ==========================================================================
   CHANGELOG — สิ่งที่ backend เปลี่ยนเมื่อ 25 สิงหาคม 2569
   ==========================================================================

   🔴 breaking
     point-claim  `account` เลข id → object CrmMiniAccount
     point-claim  `approved_by` · `rejected_by` · `rollback_by` เลข/สตริง → CrmMiniUser
     loyalty-tier · loyalty-earning-policy · loyalty-reward
                  `division` เขียนไม่ได้แล้ว เติมจาก header เอง
     ทุก endpoint ตัด PUT ออก เหลือ PATCH (เราใช้ PATCH อยู่แล้ว ไม่กระทบ)

   ✅ เพิ่ม
     point-claim              `order_location` · `?name=` · `?amount__gte/lte/gt/lt`
     loyalty-account          `full_name`
     ACTION                   `EDIT` (PENDING → PENDING แก้ยอดโดยไม่ตัดสิน)
     point-claim-action-log/  ประวัติการตรวจทั้ง division
     loyalty-reward/          ของรางวัล CRUD
     loyalty-point-summary/   คะแนนคงค้างและคะแนนใกล้หมดอายุ
     loyalty-tier-summary/    จำนวนสมาชิกต่อระดับ
     redeem-reward/ · my-redemption-history/ · my-tier-points-history/  (ฝั่งแอปมือถือ)

   ⚠️ สิทธิ์เปลี่ยน
     loyalty-tier · loyalty-earning-policy · loyalty-reward
     อ่านได้ทุกคนที่ผ่าน auth แต่ **เขียนได้เฉพาะผู้ใช้ที่เป็นสมาชิกของ division นั้น**
     ไม่งั้น 403 "User is not a member of the division"

   ⛔ ยังไม่มี
     รายการแลกของรางวัลฝั่งเจ้าหน้าที่ (มีแต่ my-redemption-history ของตัวเอง)
     ระบบบทบาท · ผู้รับผิดชอบคำขอ · การตรวจจับความผิดปกติ
     การแลกของรางวัลประเภทสินค้า — ตัดคะแนนแล้วแต่ยังไม่ส่งมอบอะไร
*/
